import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import './InventoryReport.css';

const LOW_STOCK_THRESHOLD = 10;

const COLOR_MAP = {
    White:'#f5f5f5', Black:'#1f1f1f', Red:'#e53935', Blue:'#1a5faa',
    Green:'#2e7d32', Navy:'#1a237e', Gray:'#888', Yellow:'#f9a825',
    Orange:'#e65100', Pink:'#e91e8c', Purple:'#7b1fa2', Brown:'#5d4037',
};

const STOCK_TABS = [
    { key: 'all',          label: 'All Products' },
    { key: 'in_stock',     label: 'In Stock' },
    { key: 'low_stock',    label: 'Low Stock' },
    { key: 'out_of_stock', label: 'Out of Stock' },
];

// ── Compute total stock from nested inventory object ──────────────────────────
// Falls back to stockQuantity field if inventory object is not present
function computeTotalStock(product) {
    if (product.inventory && typeof product.inventory === 'object') {
        return Object.values(product.inventory).reduce(
            (total, colorStock) =>
                total + Object.values(colorStock).reduce((s, v) => s + (Number(v) || 0), 0),
            0
        );
    }
    // Fallback to flat stockQuantity for products not yet updated
    return Number(product.stockQuantity) || 0;
}

function getStockStatus(total) {
    if (total === 0) return 'out_of_stock';
    if (total <= LOW_STOCK_THRESHOLD) return 'low_stock';
    return 'in_stock';
}

function formatDate(ts) {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' });
}

// ── Status Badge ──────────────────────────────────────────────────────────────
function InventoryStatusBadge({ status }) {
    const map = {
        in_stock:     { label: 'In Stock',     cls: 'badge--green'  },
        low_stock:    { label: 'Low Stock',    cls: 'badge--yellow' },
        out_of_stock: { label: 'Out of Stock', cls: 'badge--red'    },
    };
    const info = map[status] || { label: '—', cls: 'badge--gray' };
    return <span className={`status-badge ${info.cls}`}>{info.label}</span>;
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function InventoryReport() {
    const [activeTab,  setActiveTab]  = useState('all');
    const [rawProducts, setRawProducts] = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [search,     setSearch]     = useState('');
    const [filterCat,  setFilterCat]  = useState('all');

    useEffect(() => {
        fetchProducts();
    }, []);

    async function fetchProducts() {
        setLoading(true);
        try {
            // Fetch ALL products — we compute stock client-side from inventory field
            const snap = await getDocs(collection(db, 'products'));
            const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRawProducts(data);
        } catch (err) {
            console.error('Error fetching products:', err);
            setRawProducts([]);
        }
        setLoading(false);
    }

    // ── Enrich products with computed total stock ─────────────────────────────
    const products = useMemo(() =>
        rawProducts.map(p => ({
            ...p,
            computedTotal: computeTotalStock(p),
            colors: Array.isArray(p.colors) ? p.colors : [],
        })),
        [rawProducts]
    );

    // ── Unique categories for filter ──────────────────────────────────────────
    const categories = useMemo(() => {
        const cats = [...new Set(products.map(p => p.category).filter(Boolean))].sort();
        return cats;
    }, [products]);

    // ── Filter by tab + search + category ─────────────────────────────────────
    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return products.filter(p => {
            const status = getStockStatus(p.computedTotal);

            // Tab filter
            if (activeTab !== 'all' && status !== activeTab) return false;

            // Category filter
            if (filterCat !== 'all' && p.category !== filterCat) return false;

            // Search filter
            if (q) {
                return (
                    (p.name     || '').toLowerCase().includes(q) ||
                    (p.category || '').toLowerCase().includes(q) ||
                    (p.subcategory || '').toLowerCase().includes(q)
                );
            }
            return true;
        });
    }, [products, activeTab, search, filterCat]);

    // ── Stat counts (always from full product list) ───────────────────────────
    const totalProducts = products.length;
    const lowStock      = products.filter(p => getStockStatus(p.computedTotal) === 'low_stock').length;
    const outOfStock    = products.filter(p => getStockStatus(p.computedTotal) === 'out_of_stock').length;
    const inStock       = products.filter(p => getStockStatus(p.computedTotal) === 'in_stock').length;

    // ── Export ────────────────────────────────────────────────────────────────
    function handleExport() {
        const rows = filtered.map(p => ({
            'Product Name':   p.name       || '—',
            'Category':       p.category   || '—',
            'Subcategory':    p.subcategory || '—',
            'Size Tier':      p.sizeTier   || '—',
            'Colors':         (p.colors || []).join(', ') || '—',
            'Total Stock':    p.computedTotal,
            'Status':         getStockStatus(p.computedTotal).replace('_', ' '),
            'Last Updated':   formatDate(p.updatedAt),
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Inventory Report');
        const today = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `JG_Inventory_${today}.xlsx`);
    }

    return (
        <div className="report-section">

            {/* ── Header ── */}
            <div className="report-header">
                <h2 className="report-title">Inventory Report</h2>
                <button className="report-export-btn" onClick={handleExport} title="Export to Excel">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                </button>
            </div>

            {/* ── Stat Cards ── */}
            <div className="report-stat-cards report-stat-cards--4">
                <div className="stat-card stat-card--blue">
                    <span className="stat-card__label">Total Products</span>
                    <svg className="stat-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 7l9-4 9 4-9 4-9-4z"/><path d="M3 7v10l9 4 9-4V7"/><path d="M12 11v10"/>
                    </svg>
                    <span className="stat-card__value">{loading ? '—' : totalProducts}</span>
                </div>
                <div className="stat-card stat-card--green">
                    <span className="stat-card__label">In Stock</span>
                    <svg className="stat-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span className="stat-card__value">{loading ? '—' : inStock}</span>
                </div>
                <div className="stat-card stat-card--yellow">
                    <span className="stat-card__label">Low Stock</span>
                    <svg className="stat-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 20V10M12 20V4M6 20v-6"/><line x1="4" y1="22" x2="20" y2="22"/>
                    </svg>
                    <span className="stat-card__value">{loading ? '—' : lowStock}</span>
                </div>
                <div className="stat-card stat-card--red">
                    <span className="stat-card__label">Out of Stock</span>
                    <svg className="stat-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                    <span className="stat-card__value">{loading ? '—' : outOfStock}</span>
                </div>
            </div>

            {/* ── Filter bar ── */}
            <div className="inv-report-filters">
                {/* Search */}
                <div className="inv-report-search-wrap">
                    <input
                        type="text"
                        className="inv-report-search"
                        placeholder="Search products…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    <svg className="inv-report-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                    </svg>
                </div>
                {/* Category filter */}
                <select
                    className="inv-report-select"
                    value={filterCat}
                    onChange={e => setFilterCat(e.target.value)}
                >
                    <option value="all">All Categories</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {(search || filterCat !== 'all') && (
                    <button className="inv-report-clear" onClick={() => { setSearch(''); setFilterCat('all'); }}>
                        Clear
                    </button>
                )}
            </div>

            {/* ── Stock Tabs ── */}
            <div className="report-filter-tabs">
                {STOCK_TABS.map(tab => (
                    <button
                        key={tab.key}
                        className={`report-filter-tab${activeTab === tab.key ? ' is-active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                        {!loading && (
                            <span className="inv-tab-count">
                                {tab.key === 'all'          ? totalProducts
                                 : tab.key === 'in_stock'   ? inStock
                                 : tab.key === 'low_stock'  ? lowStock
                                 : outOfStock}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* ── Table ── */}
            <div className="report-table-wrapper">
                <table className="report-table">
                    <thead>
                        <tr>
                            <th>Product Image</th>
                            <th>Product Name</th>
                            <th>Category</th>
                            <th>Size Tier</th>
                            <th>Colors</th>
                            <th>Total Stock</th>
                            <th>Last Updated</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8} className="report-table__empty">Loading...</td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan={8} className="report-table__empty">No products found.</td></tr>
                        ) : (
                            filtered.map(product => {
                                const status = getStockStatus(product.computedTotal);
                                return (
                                    <tr
                                        key={product.id}
                                        className={
                                            status === 'out_of_stock' ? 'row--out-of-stock'
                                            : status === 'low_stock'  ? 'row--low-stock'
                                            : ''
                                        }
                                    >
                                        {/* Image */}
                                        <td>
                                            <div className="product-thumb">
                                                {product.imageUrl || product.image ? (
                                                    <img src={product.imageUrl || product.image} alt={product.name} />
                                                ) : (
                                                    <div className="product-thumb__placeholder">
                                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                            <rect x="3" y="3" width="18" height="18" rx="2"/>
                                                            <circle cx="8.5" cy="8.5" r="1.5"/>
                                                            <polyline points="21 15 16 10 5 21"/>
                                                        </svg>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        {/* Name */}
                                        <td className="product-name">{product.name || '—'}</td>
                                        {/* Category */}
                                        <td>{product.category || '—'}</td>
                                        {/* Size Tier */}
                                        <td>{product.sizeTier || <span className="inv-no-tier">Not set</span>}</td>
                                        {/* Colors */}
                                        <td>
                                            <div className="inv-color-dots">
                                                {product.colors.length > 0
                                                    ? product.colors.map(c => (
                                                        <span
                                                            key={c}
                                                            className="inv-color-dot"
                                                            style={{
                                                                background: COLOR_MAP[c] || '#ccc',
                                                                border: c === 'White' ? '1px solid #ddd' : 'none'
                                                            }}
                                                            title={c}
                                                        />
                                                    ))
                                                    : <span className="inv-no-colors">—</span>
                                                }
                                            </div>
                                        </td>
                                        {/* Total Stock */}
                                        <td className="inv-total-stock">{product.computedTotal}</td>
                                        {/* Last Updated */}
                                        <td className="inv-updated">{formatDate(product.updatedAt)}</td>
                                        {/* Status */}
                                        <td><InventoryStatusBadge status={status} /></td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {!loading && (
                <p className="report-result-count">
                    Showing {filtered.length} product{filtered.length !== 1 ? 's' : ''}
                    {filtered.length !== products.length ? ` (filtered from ${products.length} total)` : ''}
                </p>
            )}
        </div>
    );
}